import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { FolderOpen } from 'lucide-react';
import { ChampionshipMediaKindRow } from 'modules/championship/domain/graphql/championship-media';
import {
  useAssignChampionshipTemplateGroupMutation,
  useChampionshipMediaSettingsQuery,
  useUnassignChampionshipTemplateGroupMutation,
} from 'modules/championship/store/championship-media-api';
import GenerateMediaModal from 'modules/championship/ui/components/form-parts/media/generate-media-modal';
import MediaKindStatus, { mediaKindState } from 'modules/championship/ui/components/form-parts/media/media-kind-status';
import { MediaKind } from 'modules/manager/domain/media-kind';
import { MediaSubjectType, subjectTypeFor } from 'modules/manager/domain/media-kind/media-kind-catalog';
import { useGenerateMediaMutation } from 'modules/manager/store/media-generation-jobs-api';
import { useMediaManagerTemplateGroupsQuery } from 'modules/manager/store/media-manager-api';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFeature } from 'shared/hooks/use-feature';
import { SelectOption, UDSelect } from 'ud-form/components/select/component';
import UDButton from 'ud-ui/components/button';
import { UDEntityRow } from 'ud-ui/components/paged-list';
import UDText from 'ud-ui/components/text';

interface ChampionshipMediaPageProps {
  championshipId: number;
  /**
   * Организация, которой принадлежит соревнование (`championship.tournamentId` бэкенда).
   * Паки берутся по ней, а не по активной организации в шапке: супер-админ открывает
   * турниры чужих организаций, а пак другой организации бэкенд привязать не даст (IMGM-1489).
   */
  organizerId: number;
}

/** Бэкенд отказал в привязке: пак принадлежит другой организации (`AssignChampionshipTemplateGroupHandler`). */
const FOREIGN_GROUP_ERROR = 'not owned by this organization';

/** Текст первой GraphQL-ошибки — его кладёт `graphqlBaseQuery` (`shared/api/base-api.ts`). */
function errorMessage(error: unknown): string | null {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return null;
}

/** Поверхность блока вкладки: белая карточка на фоне страницы, как у «Этапов» и «Заявок». */
const CARD_CLASS = 'bg-surfaceCard rounded-card';

const ChampionshipMediaPage = ({ championshipId, organizerId }: ChampionshipMediaPageProps) => {
  const { t } = useLingui();
  const { isEnabled, isFetching: isFeatureFetching } = useFeature('MEDIA_MANAGER');
  const { data: settings, isFetching } = useChampionshipMediaSettingsQuery(championshipId);
  const { data: groups = [] } = useMediaManagerTemplateGroupsQuery({ tournamentId: organizerId }, { skip: !organizerId });
  const [assignGroup, { isLoading: isAssigning }] = useAssignChampionshipTemplateGroupMutation();
  const [unassignGroup, { isLoading: isUnassigning }] = useUnassignChampionshipTemplateGroupMutation();
  const [generateMedia] = useGenerateMediaMutation();
  const [isChanging, setIsChanging] = useState(false);
  const [picker, setPicker] = useState<{ kind: MediaKind; subjectType: MediaSubjectType; title: string } | null>(null);
  /** Тип, чья генерация сейчас ставится в очередь: крутится только его кнопка, а не все сразу. */
  const [generatingKind, setGeneratingKind] = useState<MediaKind | null>(null);

  const groupOptions: SelectOption<number>[] = useMemo(
    () =>
      groups
        // Ответ и так по одной организации; фильтр — страховка от глобальных паков в выдаче.
        .filter((group) => group.tournamentId === organizerId)
        .map((group) => ({ value: group.id, label: `${group.name} (${group.templatesCount})` })),
    [groups, organizerId],
  );

  if (isFeatureFetching) {
    return null;
  }

  if (!isEnabled) {
    return (
      <div className="w-full px-6" data-testid="championship-media-upsell">
        <UDText type="title-3" className="mb-3">
          <Trans>Медиа-менеджер недоступен в вашей подписке</Trans>
        </UDText>
        <UDText type="subhead" className="text-inkSecondary mb-6">
          <Trans>Чтобы привязать пак шаблонов и публиковать картинки автоматически, подключите тариф с медиа-менеджером.</Trans>
        </UDText>
        <Link to={Pages.SETTINGS.builders.subscription('MEDIA_MANAGER')}>
          <UDButton variant="primary" type="button">
            <Trans>Выбрать тариф</Trans>
          </UDButton>
        </Link>
      </div>
    );
  }

  const assigned = settings?.templateGroup;
  const showPicker = !assigned || isChanging;

  const handleAssign = async (option: SelectOption<number> | null) => {
    if (!option) return;
    try {
      await assignGroup({ championshipId, templateGroupId: option.value }).unwrap();
      setIsChanging(false);
    } catch (error) {
      console.error('Failed to assign media template group:', error);
      const message = errorMessage(error);
      if (message?.includes(FOREIGN_GROUP_ERROR)) {
        toast.error(t`Не удалось привязать пак шаблонов: он принадлежит другой организации`);
      } else if (message) {
        toast.error(t`Не удалось привязать пак шаблонов: ${message}`);
      } else {
        toast.error(t`Не удалось привязать пак шаблонов`);
      }
    }
  };

  const showGeneratedToast = () => {
    toast.success(
      <span>
        {t`Генерация поставлена в очередь.`}{' '}
        <Link to={Pages.MEDIA_MANAGER.QUEUE} className="underline">
          {t`Открыть очередь`}
        </Link>
      </span>,
    );
  };

  const enqueueGenerate = async (kind: MediaKind, subjectId: number) => {
    setGeneratingKind(kind);
    try {
      await generateMedia({ championshipId, kind, subjectId }).unwrap();
      showGeneratedToast();
    } catch (error) {
      console.error('Failed to generate media:', error);
      toast.error(t`Не удалось поставить генерацию в очередь`);
    } finally {
      setGeneratingKind((current) => (current === kind ? null : current));
    }
  };

  const handleGenerate = (row: ChampionshipMediaKindRow) => {
    const subjectType = (row.subjectType as MediaSubjectType) || subjectTypeFor(row.kind);
    if (subjectType === 'championship') {
      void enqueueGenerate(row.kind, championshipId);
      return;
    }
    setPicker({ kind: row.kind, subjectType, title: row.title });
  };

  const handlePickerSubmit = (subjectId: number) => {
    if (!picker) return;
    const kind = picker.kind;
    setPicker(null);
    void enqueueGenerate(kind, subjectId);
  };

  const handleUnassign = async () => {
    try {
      await unassignGroup(championshipId).unwrap();
      setIsChanging(false);
    } catch (error) {
      console.error('Failed to unassign media template group:', error);
      toast.error(t`Не удалось отвязать пак шаблонов`);
    }
  };

  const kindSubtitle = (row: ChampionshipMediaKindRow) => {
    switch (mediaKindState(row)) {
      case 'ready': {
        const templateName = row.templateName;
        return t`Шаблон «${templateName}»`;
      }
      case 'missing':
        return t`Добавьте в пак шаблон этого типа`;
      default:
        return t`Генерация этого типа появится позже`;
    }
  };

  return (
    <div className="flex w-full flex-col gap-6 px-6 pb-10" data-testid="championship-media-page">
      {isFetching && !settings ? (
        <div className="skeleton-block h-24 w-full" data-testid="championship-media-skeleton" />
      ) : assigned && !isChanging ? (
        <section className={`${CARD_CLASS} flex items-center gap-4 p-5`} data-testid="assigned-group-card">
          <div className="bg-surfaceSunken rounded-card text-inkSecondary flex h-12 w-12 shrink-0 items-center justify-center">
            <FolderOpen size={22} aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <UDText type="footnote" className="text-inkSecondary">
              <Trans>Пак шаблонов</Trans>
            </UDText>
            <UDText type="subhead" className="text-inkPrimary truncate font-semibold">
              {assigned.name}
            </UDText>
            <UDText type="footnote" className="text-inkMuted">
              {plural(assigned.templatesCount, { one: '# шаблон', few: '# шаблона', many: '# шаблонов', other: '# шаблонов' })}
            </UDText>
          </div>
          <div className="flex shrink-0 gap-3">
            <UDButton variant="secondary" type="button" onClick={() => setIsChanging(true)} disabled={isUnassigning}>
              <Trans>Сменить</Trans>
            </UDButton>
            <UDButton variant="secondary" type="button" onClick={() => void handleUnassign()} loading={isUnassigning}>
              <Trans>Отвязать</Trans>
            </UDButton>
          </div>
        </section>
      ) : null}

      {showPicker && (
        <div className="max-w-xl" data-testid="championship-media-picker">
          <UDText type="title-3" className="mb-2">
            <Trans>Пак шаблонов</Trans>
          </UDText>
          <UDText type="subhead" className="text-inkSecondary mb-6">
            <Trans>Привяжите пак шаблонов, чтобы потом включить автогенерацию картинок по событиям турнира.</Trans>
          </UDText>
          {groupOptions.length === 0 ? (
            <UDText type="subhead" className="text-inkSecondary">
              <Trans>У организации ещё нет паков шаблонов.</Trans>{' '}
              <Link to={Pages.MEDIA_MANAGER.INDEX} className="text-inkPrimary underline">
                <Trans>Открыть медиа-менеджер</Trans>
              </Link>
            </UDText>
          ) : (
            <UDSelect
              placeholder={t`Выберите пак шаблонов`}
              options={groupOptions}
              isDisabled={isAssigning}
              onChange={(value) => void handleAssign(value as SelectOption<number> | null)}
            />
          )}
        </div>
      )}

      {settings && assigned ? (
        <section className="flex flex-col gap-3" data-testid="championship-media-kinds">
          <div>
            <UDText type="title-3">
              <Trans>Типы медиа</Trans>
            </UDText>
            <UDText type="subhead" className="text-inkSecondary">
              <Trans>Картинки, которые можно сгенерировать по шаблонам пака.</Trans>
            </UDText>
          </div>
          <div className="flex flex-col gap-2.5" role="list">
            {settings.kinds.map((row) => {
              const state = mediaKindState(row);
              return (
                <UDEntityRow
                  key={row.kind}
                  data-testid={`media-kind-row-${row.kind}`}
                  title={row.title}
                  subtitle={kindSubtitle(row)}
                  actions={
                    <>
                      <MediaKindStatus state={state} />
                      {state === 'ready' ? (
                        <UDButton
                          variant="secondary"
                          type="button"
                          onClick={() => handleGenerate(row)}
                          loading={generatingKind === row.kind}
                          data-testid={`generate-media-${row.kind}`}
                        >
                          <Trans>Сгенерировать</Trans>
                        </UDButton>
                      ) : null}
                    </>
                  }
                />
              );
            })}
          </div>
        </section>
      ) : null}

      {picker ? (
        <GenerateMediaModal
          championshipId={championshipId}
          kind={picker.kind}
          subjectType={picker.subjectType}
          kindTitle={picker.title}
          onClose={() => setPicker(null)}
          onSubmit={handlePickerSubmit}
        />
      ) : null}
    </div>
  );
};

export default ChampionshipMediaPage;
